import { Button } from '@factorialco/deprecated-design-system'
import { type SelectOption } from '@factorialco/deprecated-design-system'
import {
  EmployeeBlocksEmployeeTaxResidenceFragment,
  EmployeeBlocksEmployeeTaxResidenceFragmentDoc,
  EmployeeBlocksUpsertEmployeeTaxResidenceDocument,
} from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import FormComponent from 'modules/core/components/Form'
import FormRow from 'modules/core/components/FormRow'
import InlineTooltip from 'modules/core/components/InlineTooltip'
import Country from 'modules/core/components/Inputs/Country'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import type { ScrollRowType } from 'modules/employees/components/blocks/TaxResidence/types'
import TaxResidenceFormData from 'modules/employees/lib/forms/Personal/TaxResidence'

interface Props {
  employeeId: number
  employeeTaxResidence?: EmployeeBlocksEmployeeTaxResidenceFragment | null
  isSpanish: boolean
  readonly: boolean
  scrollTo: ScrollRowType | null | undefined
  taxResidenceForm: TaxResidenceFormData
}

const TaxResidenceForm = ({
  employeeId,
  employeeTaxResidence,
  isSpanish,
  readonly,
  scrollTo = null,
  taxResidenceForm,
}: Props) => {
  const shouldScrollToRow = (type: ScrollRowType | null | undefined): boolean => {
    return scrollTo === type
  }

  const [upsertTaxResidence] = useMutation(EmployeeBlocksUpsertEmployeeTaxResidenceDocument, {
    extractorFn: (data) => data.hiringVersionsMutations.upsertHiringversionsTaxResidence,
    update: (cache, result) => {
      const updatedTaxResidence =
        result.data?.hiringVersionsMutations?.upsertHiringversionsTaxResidence?.taxResidence
      if (employeeTaxResidence && updatedTaxResidence) {
        cache.writeFragment({
          id: cache.identify(employeeTaxResidence),
          fragment: EmployeeBlocksEmployeeTaxResidenceFragmentDoc,
          fragmentName: 'EmployeeBlocksEmployeeTaxResidence',
          data: {
            ...employeeTaxResidence,
            isResident: updatedTaxResidence.isResident,
            cityOfBirth: updatedTaxResidence.cityOfBirth,
            countryOfBirth: updatedTaxResidence.countryOfBirth,
          },
        })
      }
    },
  })

  const onSubmit = async () => {
    const result = await upsertTaxResidence({
      variables: {
        cityOfBirth: taxResidenceForm.get('city_of_birth').value ?? '',
        countryOfBirth: taxResidenceForm.get('country_of_birth').value ?? '',
        employeeId: employeeId,
        isResident: taxResidenceForm.get('is_resident').value ?? true,
      },
    })

    const handled = handleResult(result, (errors) => {
      if (errors) {
        app.setFlash({
          type: 'wrong',
          message: i18n.t('profile.employee.tax_residence.update_tax_residence_data_error'),
        })
      }
    })

    if (handled.success) {
      taxResidenceForm.cleanAll()
      app.setFlash({
        type: 'correct',
        message: i18n.t('profile.employee.tax_residence.update_tax_residence_data'),
      })
    }

    return handled
  }

  const residentOptions: SelectOption[] = [
    {
      value: true,
      label: i18n.t('employee_form.tax_residence.resident'),
    },
    {
      value: false,
      label: i18n.t('employee_form.tax_residence.not_resident'),
    },
  ]

  const isNotResident =
    taxResidenceForm.get('is_resident').value === false ||
    taxResidenceForm.get('is_resident').value === 'false'

  return (
    <InlineTooltip>
      <FormComponent>
        <FormRow>
          <Select
            mandatory
            field={taxResidenceForm.get('is_resident')}
            label={i18n.t('employee_form.is_resident')}
            none={i18n.t('common.choose')}
            options={residentOptions}
            readonly={readonly}
          />
        </FormRow>
        {isNotResident && (
          <FormRow multi scrollToRow={shouldScrollToRow('city_of_birth')}>
            <Text
              mandatory
              label={i18n.t('employee_form.tax_residence.city_of_birth')}
              field={taxResidenceForm.get('city_of_birth')}
              readonly={readonly}
            />
            <Country
              mandatory
              label={i18n.t('employee_form.tax_residence.country_of_birth')}
              field={taxResidenceForm.get('country_of_birth')}
              readonly={readonly}
            />
          </FormRow>
        )}
        {(taxResidenceForm.isDirty || (!employeeTaxResidence && isSpanish)) && (
          <FormRow footer>
            <Button onClick={onSubmit}>{i18n.t('common.submit')}</Button>
          </FormRow>
        )}
      </FormComponent>
    </InlineTooltip>
  )
}

export default observer(TaxResidenceForm)
